/**
 * Componente para mapear uma referência externa individual
 * Permite selecionar uma entidade do tenant atual ou deixar não mapeado
 * Inclui botão "+" para criação inline quando não há candidatos
 * Inclui opção "Criar automático" para variáveis
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Fase 4: Wizard de Mapeamento
 * RELEASE: 1.1.0 - 2026-03-22 - Botão "+" para criação inline de entidades
 * RELEASE: 1.2.0 - 2026-03-22 - Opção "Criar automático" para variáveis
 */

import { useMemo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Check, AlertTriangle, XCircle, Plus, Wand2, Image, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExternalReference, ExternalReferenceType, AutoMatchResult } from '@/lib/flows/reference-types';
import { MediaGalleryDialog } from '@/components/tenant/media/MediaGalleryDialog';
import type { MediaGalleryItem } from '@/types/media';

/** Valor especial para criação automática */
export const AUTO_CREATE_VALUE = '__auto_create__';

/** Candidato para mapeamento */
interface MappingCandidate {
  id: string;
  name: string;
  similarity: number;
}

interface ReferenceMappingItemProps {
  /** Referência externa a ser mapeada */
  reference: ExternalReference;
  /** Resultado do auto-match (se disponível) */
  autoMatch?: AutoMatchResult;
  /** ID atualmente selecionado */
  selectedId: string | null;
  /** Lista de candidatos disponíveis no tenant */
  candidates: MappingCandidate[];
  /** Callback quando o mapeamento muda */
  onChange: (placeholder: string, mappedId: string | null) => void;
  /** Se a referência é obrigatória */
  required?: boolean;
  /** Callback para criar nova entidade desse tipo (botão "+") */
  onCreateNew?: () => void;
  /** Se deve mostrar opção "Criar automático" no dropdown */
  showAutoCreate?: boolean;
  /** Tipo da referência para renderização condicional (ex: media_gallery) */
  referenceType?: ExternalReferenceType;
}

/**
 * Item individual de mapeamento de referência
 * Mostra a referência original e permite selecionar equivalente
 */
export function ReferenceMappingItem({
  reference,
  autoMatch,
  selectedId,
  candidates,
  onChange,
  required = false,
  onCreateNew,
  showAutoCreate = false,
  referenceType,
}: ReferenceMappingItemProps) {
  // Estado para controlar abertura do dialog de galeria de mídia
  const [galleryOpen, setGalleryOpen] = useState(false);
  // Determina status do mapeamento
  const status = useMemo(() => {
    if (selectedId === AUTO_CREATE_VALUE) return 'auto_create';
    if (selectedId) return 'mapped';
    if (required) return 'required';
    return 'unmapped';
  }, [selectedId, required]);

  // Ícone de status
  const StatusIcon = useMemo(() => {
    switch (status) {
      case 'mapped':
        return Check;
      case 'auto_create':
        return Wand2;
      case 'required':
        return XCircle;
      default:
        return AlertTriangle;
    }
  }, [status]);

  // Cor do status
  const statusColor = useMemo(() => {
    switch (status) {
      case 'mapped':
        return 'text-green-500';
      case 'auto_create':
        return 'text-primary';
      case 'required':
        return 'text-destructive';
      default:
        return 'text-yellow-500';
    }
  }, [status]);

  // Ordena candidatos por similaridade se houver auto-match
  const sortedCandidates = useMemo(() => {
    if (!autoMatch || autoMatch.candidates.length === 0) {
      return candidates;
    }

    // Cria mapa de similaridade
    const similarityMap = new Map(
      autoMatch.candidates.map(c => [c.id, c.similarity])
    );

    // Ordena por similaridade (maior primeiro)
    return [...candidates].sort((a, b) => {
      const simA = similarityMap.get(a.id) ?? 0;
      const simB = similarityMap.get(b.id) ?? 0;
      return simB - simA;
    });
  }, [candidates, autoMatch]);

  // Handler para mudança de seleção
  const handleChange = (value: string) => {
    if (value === '__none__') {
      onChange(reference.placeholder, null);
    } else if (value === AUTO_CREATE_VALUE) {
      onChange(reference.placeholder, AUTO_CREATE_VALUE);
    } else {
      onChange(reference.placeholder, value);
    }
  };

  // Valor atual do select (considera __auto_create__)
  const selectValue = selectedId || '__none__';

  // Nome do arquivo selecionado na galeria (busca do candidato correspondente)
  const selectedMediaName = useMemo(() => {
    if (!selectedId || referenceType !== 'media_gallery') return null;
    const candidate = candidates.find(c => c.id === selectedId);
    return candidate?.name || null;
  }, [selectedId, candidates, referenceType]);

  /**
   * Handler para seleção de mídia na galeria
   * Recebe o item selecionado e atualiza o mapeamento
   */
  const handleGallerySelect = (media: MediaGalleryItem) => {
    onChange(reference.placeholder, media.id);
    setGalleryOpen(false);
  };

  /**
   * Handler para limpar seleção de mídia da galeria
   */
  const handleClearMedia = () => {
    onChange(reference.placeholder, null);
  };

  return (
    <div className="flex items-center gap-3 py-2.5 px-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
      {/* Indicador de status */}
      <StatusIcon className={cn('h-4 w-4 shrink-0', statusColor)} />

      {/* Info da referência original */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{reference.name}</span>
          {required && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
              Obrigatório
            </Badge>
          )}
        </div>
        {reference.context && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {reference.context}
          </p>
        )}
      </div>

      {/* ============================================================= */}
      {/* Renderização condicional: Galeria de mídia vs Select padrão   */}
      {/* ============================================================= */}

      {referenceType === 'media_gallery' ? (
        <>
          {/* Modo galeria de mídia: botão de seleção + preview do arquivo */}
          {selectedId && selectedMediaName ? (
            /* Arquivo selecionado — mostra nome + botões de trocar/limpar */
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-muted/50 max-w-[200px]">
                <Image className="h-4 w-4 shrink-0 text-primary" />
                <span className="text-sm truncate">{selectedMediaName}</span>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0"
                    onClick={() => setGalleryOpen(true)}
                  >
                    Trocar
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Selecionar outro arquivo da galeria</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={handleClearMedia}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remover seleção</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            /* Nenhum arquivo selecionado — botão para abrir galeria */
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-2 shrink-0"
              onClick={() => setGalleryOpen(true)}
            >
              <Image className="h-4 w-4" />
              Selecionar da galeria
            </Button>
          )}

          {/* Dialog da galeria de mídia */}
          <MediaGalleryDialog
            open={galleryOpen}
            onOpenChange={setGalleryOpen}
            selectionMode={true}
            onSelect={handleGallerySelect}
          />
        </>
      ) : (
        <>
          {/* Modo padrão: Select dropdown com candidatos */}
          <Select
            value={selectValue}
            onValueChange={handleChange}
          >
            <SelectTrigger className="w-[200px] h-9">
              <SelectValue placeholder="Selecionar..." />
            </SelectTrigger>
            <SelectContent>
              {/* Opção "Criar automático" para variáveis */}
              {showAutoCreate && (
                <SelectItem value={AUTO_CREATE_VALUE} className="font-medium">
                  <span className="flex items-center gap-2 text-primary">
                    <Wand2 className="h-3.5 w-3.5" />
                    Criar automático
                  </span>
                </SelectItem>
              )}

              {/* Opção para deixar não mapeado */}
              <SelectItem value="__none__" className="text-muted-foreground">
                <span className="flex items-center gap-2">
                  <XCircle className="h-3.5 w-3.5" />
                  Não mapear
                </span>
              </SelectItem>

              {/* Candidatos disponíveis */}
              {sortedCandidates.map((candidate) => {
                const similarity = autoMatch?.candidates.find(c => c.id === candidate.id)?.similarity;
                const isExactMatch = similarity === 1;
                const isGoodMatch = similarity && similarity >= 0.7;

                return (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    <span className="flex items-center gap-2">
                      <span className="truncate max-w-[140px]">{candidate.name}</span>
                      {isExactMatch && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 bg-green-500/10 text-green-600">
                          Exato
                        </Badge>
                      )}
                      {!isExactMatch && isGoodMatch && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0">
                          {Math.round((similarity ?? 0) * 100)}%
                        </Badge>
                      )}
                    </span>
                  </SelectItem>
                );
              })}

              {/* Mensagem se não há candidatos */}
              {sortedCandidates.length === 0 && !showAutoCreate && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
                  Nenhuma opção disponível
                </div>
              )}
            </SelectContent>
          </Select>

          {/* Botão "+" para criar nova entidade — só aparece quando tem callback */}
          {onCreateNew && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={onCreateNew}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Cadastrar novo</TooltipContent>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
}
